/**
 * @remarks RuntimeDocumentV1 组件、Reaction 与引用安全审计
 * @module @yimoka/designable-playground/runtime
 */

import { DEFAULT_FORMILY_COMPONENT_NAME_ALIASES } from '../shared/formilyCompatibility';
import {
  decodeJsonPointerSegment,
  FORMILY_SCHEMA_ARRAY_CHILD_KEYS,
  FORMILY_SCHEMA_RECORD_CHILD_KEYS,
  FORMILY_SCHEMA_SINGLE_CHILD_KEYS,
  UNSAFE_SCHEMA_OBJECT_KEYS,
  validateSchemaSecurity,
  visitFormilySchemaNodes,
} from '../shared/schema';

import {
  validateRuntimeDataBindingNode,
  validateRuntimeI18nFieldMap,
} from './document-bindings';
import {
  DATA_BINDING_MANUAL_KEYS,
  RUNTIME_UPLOAD_NETWORK_KEYS,
  RUNTIME_SEARCH_REQUEST_PROP_KEYS,
  RUNTIME_NAVIGATION_PROP_KEYS,
  isObjectRecord,
  pushRuntimeIssue,
  validateRuntimeRequestConfig,
} from './document-core';

import type {
  IRuntimeDocumentValidationIssue,
} from './document-core';
import {
  isRuntimeSchemaExpression,
  validateRuntimeStaticComponentNames,
  RUNTIME_AUTO_NETWORK_PROP_KEYS,
  RUNTIME_COMPONENT_AUTO_NETWORK_PROP_KEYS,
  RUNTIME_FIELD_VALUE_AUTO_NETWORK_COMPONENTS,
  RUNTIME_REACTION_FIELD_VALUE_KEYS,
  RUNTIME_SAFE_NATIVE_COMPONENT_TAGS,
  RUNTIME_COMPONENT_CALLABLE_EXPRESSION_PROP_PATHS,
  RUNTIME_COMPONENT_CALLABLE_EXPRESSION_PROP_KEY_PATHS,
  RUNTIME_DECORATOR_CALLABLE_EXPRESSION_PROP_PATHS,
  RUNTIME_DECORATOR_CALLABLE_EXPRESSION_PROP_KEY_PATHS,
  RUNTIME_SCHEMA_CALLABLE_EXPRESSION_KEYS,
  RUNTIME_STYLE_PROP_TREE_KEYS,
  findRuntimeSchemaExpressionPaths,
  findRuntimeDirectExpressionPaths,
  findRuntimeNonEmptyStringKeyPaths,
  findRuntimeCallableKeyExpressionPaths,
  validateRuntimeCallablePropPaths,
  validateRuntimeCallablePropKeyPaths,
  validateRuntimeExpressionCapabilities,
} from './document-expressions';
import {
  isSafeRuntimeNavigationHref,
  isSafeRuntimeNavigationPing,
  resolveRuntimeNavigationHref,
} from './navigation';

/** 在组件属性中定位原始 HTML 标记，避免薄适配器把可执行 HTML 透传到 DOM。 */
export const findUnsafeRuntimeHtmlPath = (
  value: unknown,
  path: string,
): string | undefined => {
  if (Array.isArray(value)) {
    for (const [index, item] of value.entries()) {
      const unsafePath = findUnsafeRuntimeHtmlPath(item, `${path}[${index}]`);
      if (unsafePath) return unsafePath;
    }
    return undefined;
  }
  if (!isObjectRecord(value)) return undefined;
  for (const [key, item] of Object.entries(value)) {
    const itemPath = `${path}.${key}`;
    if (key === 'dangerouslySetInnerHTML' || key === '__html') return itemPath;
    const unsafePath = findUnsafeRuntimeHtmlPath(item, itemPath);
    if (unsafePath) return unsafePath;
  }
  return undefined;
};

/** 定位会把数据表达式交给浏览器自动联网的资源或 CSS 属性。 */
export const findRuntimeAutoNetworkExpressionPaths = (
  value: unknown,
  path: string,
  inStyleTree = false,
  componentPropKeys?: ReadonlySet<string>,
) => {
  const paths: string[] = [];
  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      paths.push(...findRuntimeAutoNetworkExpressionPaths(
        item,
        `${path}[${index}]`,
        inStyleTree,
        componentPropKeys,
      ));
    });
    return paths;
  }
  if (!isObjectRecord(value)) return paths;
  Object.entries(value).forEach(([key, item]) => {
    const itemPath = `${path}.${key}`;
    const nextInStyleTree = inStyleTree || RUNTIME_STYLE_PROP_TREE_KEYS.has(key);
    if (
      typeof item === 'string'
      && isRuntimeSchemaExpression(item)
      && (
        nextInStyleTree
        || RUNTIME_AUTO_NETWORK_PROP_KEYS.has(key)
        || componentPropKeys?.has(key)
      )
    ) {
      paths.push(itemPath);
      return;
    }
    paths.push(...findRuntimeAutoNetworkExpressionPaths(
      item,
      itemPath,
      nextInStyleTree,
      componentPropKeys,
    ));
  });
  return paths;
};

/** 收集 reactions 在 fulfill 或 otherwise 中声明的动态 Schema 补丁，供完整复用 Runtime 安全审计。 */
export const findRuntimeReactionSchemaPatches = (
  value: unknown,
  path: string,
): Array<{
  path: string;
  state?: unknown;
  statePath: string;
  value: unknown;
}> => {
  if (Array.isArray(value)) {
    return value.flatMap((item, index) => (
      findRuntimeReactionSchemaPatches(item, `${path}[${index}]`)
    ));
  }
  if (!isObjectRecord(value)) return [];
  return (['fulfill', 'otherwise'] as const).flatMap((branchName) => {
    const branch = value[branchName];
    if (!isObjectRecord(branch) || !Object.prototype.hasOwnProperty.call(branch, 'schema')) {
      return [];
    }
    return [{
      path: `${path}.${branchName}.schema`,
      state: branch.state,
      statePath: `${path}.${branchName}.state`,
      value: branch.schema,
    }];
  });
};

/** 收集 reactions 每个分支的状态与可选 Schema 补丁，供按最终组件身份审计动态属性。 */
export const findRuntimeReactionStateBranches = (
  value: unknown,
  path: string,
): Array<{
  schema?: unknown;
  state: Record<string, unknown>;
  statePath: string;
}> => {
  if (Array.isArray(value)) {
    return value.flatMap((item, index) => (
      findRuntimeReactionStateBranches(item, `${path}[${index}]`)
    ));
  }
  if (!isObjectRecord(value)) return [];
  return (['fulfill', 'otherwise'] as const).flatMap((branchName) => {
    const branch = value[branchName];
    if (!isObjectRecord(branch) || !isObjectRecord(branch.state)) return [];
    return [{
      schema: branch.schema,
      state: branch.state,
      statePath: `${path}.${branchName}.state`,
    }];
  });
};

/** 仅在当前 Runtime Schema 根内解析 $ref，禁止外部引用和原型链路径。 */
export const resolveRuntimeLocalSchemaReference = (
  schemaRoot: unknown,
  reference: string,
): Record<string, unknown> | undefined => {
  if (reference === '#') return isObjectRecord(schemaRoot) ? schemaRoot : undefined;
  if (!reference.startsWith('#/')) return undefined;
  let current: unknown = schemaRoot;
  for (const rawSegment of reference.slice(2).split('/')) {
    const segment = decodeJsonPointerSegment(rawSegment);
    if (segment === undefined || UNSAFE_SCHEMA_OBJECT_KEYS.has(segment)) return undefined;
    if (Array.isArray(current)) {
      if (!/^(?:0|[1-9]\d*)$/u.test(segment)) return undefined;
      current = current[Number(segment)];
      continue;
    }
    if (!isObjectRecord(current) || !Object.prototype.hasOwnProperty.call(current, segment)) {
      return undefined;
    }
    current = current[segment];
  }
  return isObjectRecord(current) ? current : undefined;
};

export interface IRuntimeSchemaAuditNameResolution {
  declared: boolean;
  name?: string;
}

/** 把历史 Formily 组件别名归一为 Transformer 实际输出的运行时身份。 */
export const normalizeRuntimeComponentAuditName = (componentName: string | undefined) => (
  componentName === undefined
    ? undefined
    : (DEFAULT_FORMILY_COMPONENT_NAME_ALIASES[componentName] ?? componentName)
);

/** 沿本地 $ref 链查找组件或装饰器声明，并用集合阻断循环引用。 */
export const resolveRuntimeReferencedSchemaAuditName = (
  schemaRoot: unknown,
  node: Record<string, unknown>,
  propertyName: 'x-component' | 'x-decorator',
  visited: Set<Record<string, unknown>>,
): IRuntimeSchemaAuditNameResolution => {
  if (Object.prototype.hasOwnProperty.call(node, propertyName)) {
    const declaredName = node[propertyName];
    return {
      declared: true,
      name: typeof declaredName === 'string' ? declaredName : undefined,
    };
  }
  if (visited.has(node)) return { declared: false };
  visited.add(node);
  const reference = node.$ref;
  if (typeof reference !== 'string') return { declared: false };
  const referencedNode = resolveRuntimeLocalSchemaReference(schemaRoot, reference);
  if (!referencedNode) return { declared: false };
  return resolveRuntimeReferencedSchemaAuditName(
    schemaRoot,
    referencedNode,
    propertyName,
    visited,
  );
};

/** 解析节点或其本地 $ref 的有效组件身份；显式非法声明不能回退继承。 */
export const resolveRuntimeSchemaAuditName = (
  schemaRoot: unknown,
  node: Record<string, unknown>,
  propertyName: 'x-component' | 'x-decorator',
  inheritedName: string | undefined,
  allowInherited: boolean,
) => {
  const resolution = resolveRuntimeReferencedSchemaAuditName(
    schemaRoot,
    node,
    propertyName,
    new Set(),
  );
  if (resolution.declared) {
    return propertyName === 'x-component'
      ? normalizeRuntimeComponentAuditName(resolution.name)
      : resolution.name;
  }
  if (!allowInherited) return undefined;
  return propertyName === 'x-component'
    ? normalizeRuntimeComponentAuditName(inheritedName)
    : inheritedName;
};

/** 按 Formily Schema 补丁语义递归合并对象，数组和标量由动态分支整体替换。 */
export const mergeRuntimeSchemaPatch = (
  base: unknown,
  patch: unknown,
): unknown => {
  if (!isObjectRecord(base) || !isObjectRecord(patch)) return patch;
  const merged: Record<string, unknown> = { ...base };
  Object.entries(patch).forEach(([key, patchValue]) => {
    merged[key] = mergeRuntimeSchemaPatch(base[key], patchValue);
  });
  return merged;
};

/** 沿本地 $ref 链递归合并引用定义与当前节点，得到 Formily 实际消费的有效节点。 */
export const resolveRuntimeSchemaAuditNode = (
  schemaRoot: unknown,
  node: Record<string, unknown>,
  visited = new Set<Record<string, unknown>>(),
): Record<string, unknown> => {
  if (visited.has(node)) return node;
  visited.add(node);
  const reference = node.$ref;
  if (typeof reference !== 'string') return node;
  const referencedNode = resolveRuntimeLocalSchemaReference(schemaRoot, reference);
  if (!referencedNode) return node;
  const resolvedReference = resolveRuntimeSchemaAuditNode(
    schemaRoot,
    referencedNode,
    visited,
  );
  const mergedNode = mergeRuntimeSchemaPatch(resolvedReference, node);
  return isObjectRecord(mergedNode) ? mergedNode : node;
};

/** 收集 reactions 状态映射中会直接成为字段值的表达式路径。 */
export const findRuntimeReactionFieldValueExpressionPaths = (
  value: unknown,
  path: string,
): string[] => {
  if (Array.isArray(value)) {
    return value.flatMap((item, index) => (
      findRuntimeReactionFieldValueExpressionPaths(item, `${path}[${index}]`)
    ));
  }
  if (!isObjectRecord(value)) return [];
  return (['fulfill', 'otherwise'] as const).flatMap((branchName) => {
    const branch = value[branchName];
    if (!isObjectRecord(branch)) return [];
    const { state } = branch;
    if (!isObjectRecord(state)) return [];
    return [...RUNTIME_REACTION_FIELD_VALUE_KEYS].flatMap((key) => {
      if (!Object.prototype.hasOwnProperty.call(state, key)) return [];
      return findRuntimeSchemaExpressionPaths(
        state[key],
        `${path}.${branchName}.state.${key}`,
      );
    });
  });
};

/** 收集 reactions 状态映射中的动态 validator，阻断宿主函数进入 Formily 字段校验器。 */
export const findRuntimeReactionValidatorExpressionPaths = (
  value: unknown,
  path: string,
): string[] => {
  if (Array.isArray(value)) {
    return value.flatMap((item, index) => (
      findRuntimeReactionValidatorExpressionPaths(item, `${path}[${index}]`)
    ));
  }
  if (!isObjectRecord(value)) return [];
  return (['fulfill', 'otherwise'] as const).flatMap((branchName) => {
    const branch = value[branchName];
    if (!isObjectRecord(branch)) return [];
    const { state } = branch;
    if (!isObjectRecord(state)) return [];
    const { validator } = state;
    const validatorPath = `${path}.${branchName}.state.validator`;
    return [...new Set([
      ...findRuntimeDirectExpressionPaths(validator, validatorPath),
      ...findRuntimeCallableKeyExpressionPaths(
        validator,
        validatorPath,
        RUNTIME_SCHEMA_CALLABLE_EXPRESSION_KEYS,
      ),
    ])];
  });
};

/** 只保留当前 Schema 节点本身，避免合并态安全复查重复遍历原有子树和 reactions。 */
export const createRuntimeSchemaNodeAuditSnapshot = (
  node: Record<string, unknown>,
) => {
  const snapshot = { ...node };
  [
    ...FORMILY_SCHEMA_RECORD_CHILD_KEYS,
    ...FORMILY_SCHEMA_SINGLE_CHILD_KEYS,
    ...FORMILY_SCHEMA_ARRAY_CHILD_KEYS,
    'items',
    'x-reactions',
  ].forEach((key) => {
    delete snapshot[key];
  });
  return snapshot;
};

/** 递归阻断 Runtime 组件绕过宿主执行器或注入原始 HTML 的属性。 */
export const validateRuntimeComponentSecurity = (
  value: unknown,
  issues: IRuntimeDocumentValidationIssue[],
  path: string,
  inheritedContext?: {
    componentName?: string;
    decoratorName?: string;
    schemaRoot?: unknown;
  },
) => {
  const schemaRoot = inheritedContext?.schemaRoot ?? value;
  validateRuntimeExpressionCapabilities(value, issues, path);
  validateSchemaSecurity(value, path).forEach((issue) => {
    if (issue.code !== 'risky_expression') return;
    pushRuntimeIssue(
      issues,
      'invalid-runtime-document',
      issue.path,
      'Runtime 表达式不允许访问浏览器全局对象或动态执行能力',
    );
  });
  visitFormilySchemaNodes(value, path, ({ node, path: nodePath }) => {
    const isRootNode = nodePath === path;
    const auditNode = resolveRuntimeSchemaAuditNode(schemaRoot, node);
    const componentName = resolveRuntimeSchemaAuditName(
      schemaRoot,
      auditNode,
      'x-component',
      inheritedContext?.componentName,
      isRootNode,
    );
    const decoratorName = resolveRuntimeSchemaAuditName(
      schemaRoot,
      auditNode,
      'x-decorator',
      inheritedContext?.decoratorName,
      isRootNode,
    );
    const reactions = auditNode['x-reactions'];
    findRuntimeDirectExpressionPaths(reactions, `${nodePath}.x-reactions`).forEach((reactionPath) => {
      pushRuntimeIssue(
        issues,
        'invalid-runtime-document',
        reactionPath,
        'Runtime v1 不允许 x-reactions 直接执行表达式函数，请使用声明式 reactions 对象',
      );
    });
    findRuntimeNonEmptyStringKeyPaths(
      reactions,
      `${nodePath}.x-reactions`,
      new Set(['run']),
    ).forEach((reactionPath) => {
      pushRuntimeIssue(
        issues,
        'invalid-runtime-document',
        reactionPath,
        'Runtime v1 不允许 x-reactions.run 执行脚本，请使用声明式状态映射',
      );
    });
    findRuntimeReactionSchemaPatches(reactions, `${nodePath}.x-reactions`).forEach((patch) => {
      validateRuntimeStaticComponentNames(patch.value, issues, patch.path);
      // reactions 的 schema 是合入当前节点的补丁，缺省组件身份必须继承后再执行同一套安全审计。
      validateRuntimeComponentSecurity(patch.value, issues, patch.path, {
        componentName,
        decoratorName,
        schemaRoot,
      });
      if (!isObjectRecord(patch.value)) return;
      const mergedNode = mergeRuntimeSchemaPatch(auditNode, patch.value);
      if (!isObjectRecord(mergedNode)) return;
      const mergedComponentName = resolveRuntimeSchemaAuditName(
        schemaRoot,
        mergedNode,
        'x-component',
        componentName,
        true,
      );
      const mergedDecoratorName = resolveRuntimeSchemaAuditName(
        schemaRoot,
        mergedNode,
        'x-decorator',
        decoratorName,
        true,
      );
      if (
        mergedComponentName !== componentName
        || mergedDecoratorName !== decoratorName
      ) {
        // 嵌套对象可能只被补丁部分覆盖；身份变化时必须无条件按完整递归合并结果复查。
        validateRuntimeComponentSecurity(
          createRuntimeSchemaNodeAuditSnapshot(mergedNode),
          issues,
          patch.path,
          {
            componentName: mergedComponentName,
            decoratorName: mergedDecoratorName,
            schemaRoot,
          },
        );
      }
      const reactionState = patch.state;
      if (
        mergedComponentName
        && RUNTIME_FIELD_VALUE_AUTO_NETWORK_COMPONENTS.has(mergedComponentName)
        && isObjectRecord(reactionState)
      ) {
        RUNTIME_REACTION_FIELD_VALUE_KEYS.forEach((key) => {
          if (!Object.prototype.hasOwnProperty.call(reactionState, key)) return;
          findRuntimeSchemaExpressionPaths(
            reactionState[key],
            `${patch.statePath}.${key}`,
          ).forEach((sinkPath) => {
            pushRuntimeIssue(
              issues,
              'invalid-runtime-document',
              sinkPath,
              'Runtime 数据表达式不能作为资源组件字段值触发浏览器自动联网',
            );
          });
        });
      }
    });
    findRuntimeReactionStateBranches(reactions, `${nodePath}.x-reactions`).forEach((branch) => {
      const effectiveNode = branch.schema === undefined
        ? auditNode
        : mergeRuntimeSchemaPatch(auditNode, branch.schema);
      if (!isObjectRecord(effectiveNode)) return;
      const effectiveComponentName = resolveRuntimeSchemaAuditName(
        schemaRoot,
        effectiveNode,
        'x-component',
        componentName,
        true,
      );
      const effectiveDecoratorName = resolveRuntimeSchemaAuditName(
        schemaRoot,
        effectiveNode,
        'x-decorator',
        decoratorName,
        true,
      );
      /** 解析 reaction state 最终写入的静态组件身份；动态表达式已由下方独立拒绝。 */
      const resolveReactionStateName = (
        statePropertyName: 'component' | 'decorator',
        schemaPropertyName: 'x-component' | 'x-decorator',
        fallbackName: string | undefined,
      ) => {
        if (!Object.prototype.hasOwnProperty.call(branch.state, statePropertyName)) {
          return fallbackName;
        }
        const stateValue = branch.state[statePropertyName];
        const declaredName = Array.isArray(stateValue) ? stateValue[0] : stateValue;
        if (typeof declaredName !== 'string' || isRuntimeSchemaExpression(declaredName)) {
          return undefined;
        }
        return resolveRuntimeSchemaAuditName(
          schemaRoot,
          { [schemaPropertyName]: declaredName },
          schemaPropertyName,
          fallbackName,
          true,
        );
      };
      const reactionComponentName = resolveReactionStateName(
        'component',
        'x-component',
        effectiveComponentName,
      );
      const reactionDecoratorName = resolveReactionStateName(
        'decorator',
        'x-decorator',
        effectiveDecoratorName,
      );
      (['component', 'decorator'] as const).forEach((statePropertyName) => {
        if (!Object.prototype.hasOwnProperty.call(branch.state, statePropertyName)) return;
        const stateValue = branch.state[statePropertyName];
        const tupleName = Array.isArray(stateValue) && typeof stateValue[0] === 'string'
          ? stateValue[0]
          : undefined;
        const tupleProps = Array.isArray(stateValue) && isObjectRecord(stateValue[1])
          ? stateValue[1]
          : undefined;
        if (tupleName && tupleProps) {
          const tuplePropsPath = `${branch.statePath}.${statePropertyName}[1]`;
          const schemaNameProperty = statePropertyName === 'component'
            ? 'x-component'
            : 'x-decorator';
          const schemaPropsProperty = statePropertyName === 'component'
            ? 'x-component-props'
            : 'x-decorator-props';
          const syntheticNode = {
            ...(statePropertyName === 'component' && effectiveDecoratorName
              ? { 'x-decorator': effectiveDecoratorName }
              : {}),
            ...(statePropertyName === 'decorator' && effectiveComponentName
              ? { 'x-component': effectiveComponentName }
              : {}),
            [schemaNameProperty]: tupleName,
            [schemaPropsProperty]: tupleProps,
          };
          const tupleIssues: IRuntimeDocumentValidationIssue[] = [];
          // Formily 的 [component, props] 元组会直接落到字段组件；静态 props 必须与普通节点走同一安全审计。
          validateRuntimeComponentSecurity(
            syntheticNode,
            tupleIssues,
            tuplePropsPath,
            { schemaRoot: syntheticNode },
          );
          const syntheticPropsPath = `${tuplePropsPath}.${schemaPropsProperty}`;
          tupleIssues.forEach((issue) => {
            let issuePath = issue.path;
            if (issue.path === syntheticPropsPath) {
              issuePath = tuplePropsPath;
            } else if (issue.path.startsWith(`${syntheticPropsPath}.`)) {
              issuePath = `${tuplePropsPath}${issue.path.slice(syntheticPropsPath.length)}`;
            }
            issues.push({
              ...issue,
              path: issuePath,
            });
          });
        }
        findRuntimeSchemaExpressionPaths(
          stateValue,
          `${branch.statePath}.${statePropertyName}`,
        ).forEach((expressionPath) => {
          pushRuntimeIssue(
            issues,
            'invalid-runtime-document',
            expressionPath,
            'Runtime reaction state 不允许通过表达式注入组件或装饰器',
          );
        });
      });
      ([
        ['componentProps', 'x-component-props'],
        ['decoratorProps', 'x-decorator-props'],
      ] as const).forEach(([statePropertyName, schemaPropertyName]) => {
        const stateIdentityPropertyName = statePropertyName === 'componentProps'
          ? 'component'
          : 'decorator';
        const dynamicPropsValue = branch.state[statePropertyName];
        const dynamicPropsPath = `${branch.statePath}.${statePropertyName}`;
        if (!isObjectRecord(dynamicPropsValue)) {
          // Formily 会把整体表达式的对象结果直接写入字段，内部资源、导航、回调和数据绑定属性无法再被静态审计。
          findRuntimeSchemaExpressionPaths(dynamicPropsValue, dynamicPropsPath).forEach((expressionPath) => {
            pushRuntimeIssue(
              issues,
              'invalid-runtime-document',
              expressionPath,
              'Runtime reaction state 不允许通过整体表达式注入组件或装饰器属性',
            );
          });
          if (
            Object.prototype.hasOwnProperty.call(branch.state, statePropertyName)
            || !Object.prototype.hasOwnProperty.call(branch.state, stateIdentityPropertyName)
          ) return;
        }
        const dynamicProps = isObjectRecord(dynamicPropsValue) ? dynamicPropsValue : {};
        const staticProps = effectiveNode[schemaPropertyName];
        const stateIdentity = branch.state[stateIdentityPropertyName];
        const tupleProps = Array.isArray(stateIdentity) && isObjectRecord(stateIdentity[1])
          ? stateIdentity[1]
          : {};
        const propsAfterTuple = mergeRuntimeSchemaPatch(
          isObjectRecord(staticProps) ? staticProps : {},
          tupleProps,
        );
        const mergedProps = mergeRuntimeSchemaPatch(
          isObjectRecord(propsAfterTuple) ? propsAfterTuple : {},
          dynamicProps,
        );
        const mergedRuntimeProps = isObjectRecord(mergedProps)
          ? mergedProps
          : dynamicProps;
        const syntheticNode = {
          ...(reactionComponentName ? { 'x-component': reactionComponentName } : {}),
          ...(reactionDecoratorName ? { 'x-decorator': reactionDecoratorName } : {}),
          [schemaPropertyName]: mergedRuntimeProps,
        };
        const dynamicIssues: IRuntimeDocumentValidationIssue[] = [];
        // Formily 会保留静态属性并依次应用组件元组和独立属性；即使 reaction 只切换身份，也必须审计最终合并节点。
        validateRuntimeComponentSecurity(
          syntheticNode,
          dynamicIssues,
          dynamicPropsPath,
          { schemaRoot: syntheticNode },
        );
        if (statePropertyName === 'componentProps') {
          if (
            reactionComponentName === 'I18nEditor'
            && Object.prototype.hasOwnProperty.call(dynamicProps, 'fieldMap')
          ) {
            // reaction 属性会覆盖或补充静态映射，必须按最终合并结果检查跨静态/动态的一对一约束。
            validateRuntimeI18nFieldMap(
              mergedRuntimeProps.fieldMap,
              dynamicIssues,
              `${dynamicPropsPath}.fieldMap`,
            );
          }
          // 动态属性会与静态属性一样参与 Store 取数，不能绕过 dataKey 命名空间与手填数据互斥合同。
          const dynamicBindingKeys = new Set([
            'dataKey',
            ...(reactionComponentName
              ? (DATA_BINDING_MANUAL_KEYS[reactionComponentName] ?? [])
              : []),
          ]);
          const affectsDataBinding = [...dynamicBindingKeys].some(key => (
            Object.prototype.hasOwnProperty.call(dynamicProps, key)
          ));
          if (affectsDataBinding) {
            const dataBindingAuditNode = {
              ...createRuntimeSchemaNodeAuditSnapshot(effectiveNode),
              ...(reactionComponentName ? { 'x-component': reactionComponentName } : {}),
              'x-component-props': mergedRuntimeProps,
            };
            const dataBindingIssues: IRuntimeDocumentValidationIssue[] = [];
            validateRuntimeDataBindingNode(
              dataBindingAuditNode,
              dataBindingIssues,
              dynamicPropsPath,
              reactionComponentName,
            );
            dataBindingIssues.forEach((issue) => {
              // 静态 dataKey 已在原路径审计；这里只回报动态 dataKey 或跨静态/动态的冲突。
              if (
                issue.code === 'invalid-data-key'
                && !Object.prototype.hasOwnProperty.call(dynamicProps, 'dataKey')
              ) return;
              dynamicIssues.push(issue);
            });
          }
        }
        const syntheticPropsPath = `${dynamicPropsPath}.${schemaPropertyName}`;
        dynamicIssues.forEach((issue) => {
          let issuePath = issue.path;
          if (issue.path === syntheticPropsPath) {
            issuePath = dynamicPropsPath;
          } else if (issue.path.startsWith(`${syntheticPropsPath}.`)) {
            issuePath = `${dynamicPropsPath}${issue.path.slice(syntheticPropsPath.length)}`;
          }
          issues.push({
            ...issue,
            path: issuePath,
          });
        });
      });
    });
    if (
      componentName
      && RUNTIME_FIELD_VALUE_AUTO_NETWORK_COMPONENTS.has(componentName)
    ) {
      const fieldValueExpressionPaths = [
        ...findRuntimeSchemaExpressionPaths(
          auditNode.default,
          `${nodePath}.default`,
        ),
        ...findRuntimeReactionFieldValueExpressionPaths(
          reactions,
          `${nodePath}.x-reactions`,
        ),
      ];
      fieldValueExpressionPaths.forEach((sinkPath) => {
        pushRuntimeIssue(
          issues,
          'invalid-runtime-document',
          sinkPath,
          'Runtime 数据表达式不能作为资源组件字段值触发浏览器自动联网',
        );
      });
    }
    (['x-component-props', 'x-decorator-props'] as const).forEach((propertyName) => {
      const runtimeProps = auditNode[propertyName];
      if (!isObjectRecord(runtimeProps)) return;
      const runtimePropsPath = `${nodePath}.${propertyName}`;
      const unsafeHtmlPath = findUnsafeRuntimeHtmlPath(runtimeProps, runtimePropsPath);
      if (unsafeHtmlPath) {
        pushRuntimeIssue(
          issues,
          'invalid-runtime-document',
          unsafeHtmlPath,
          'Runtime 组件或装饰器属性不允许 dangerouslySetInnerHTML 或 __html 原始 HTML',
        );
      }
      const componentPropKeys = propertyName === 'x-component-props' && componentName
        ? RUNTIME_COMPONENT_AUTO_NETWORK_PROP_KEYS[componentName]
        : undefined;
      findRuntimeAutoNetworkExpressionPaths(
        runtimeProps,
        runtimePropsPath,
        false,
        componentPropKeys,
      ).forEach((sinkPath) => {
        pushRuntimeIssue(
          issues,
          'invalid-runtime-document',
          sinkPath,
          'Runtime 数据表达式不能用于浏览器自动联网的资源或 CSS 属性',
        );
      });
    });
    const validatorConfig = auditNode['x-validator'];
    const validatorExpressionPaths = new Set([
      ...findRuntimeDirectExpressionPaths(
        validatorConfig,
        `${nodePath}.x-validator`,
      ),
      ...findRuntimeCallableKeyExpressionPaths(
        validatorConfig,
        `${nodePath}.x-validator`,
        RUNTIME_SCHEMA_CALLABLE_EXPRESSION_KEYS,
      ),
    ]);
    validatorExpressionPaths.forEach((expressionPath) => {
      pushRuntimeIssue(
        issues,
        'invalid-runtime-document',
        expressionPath,
        'Runtime v1 表达式只允许无调用、无赋值的数据读取，事件能力必须由受信组件提供',
      );
    });
    findRuntimeReactionValidatorExpressionPaths(
      reactions,
      `${nodePath}.x-reactions`,
    ).forEach((expressionPath) => {
      pushRuntimeIssue(
        issues,
        'invalid-runtime-document',
        expressionPath,
        'Runtime v1 表达式只允许无调用、无赋值的数据读取，事件能力必须由受信组件提供',
      );
    });
    const decoratorProps = auditNode['x-decorator-props'];
    if (decoratorName && isObjectRecord(decoratorProps)) {
      validateRuntimeCallablePropPaths(
        decoratorProps,
        RUNTIME_DECORATOR_CALLABLE_EXPRESSION_PROP_PATHS[decoratorName],
        `${nodePath}.x-decorator-props`,
        issues,
      );
      validateRuntimeCallablePropKeyPaths(
        decoratorProps,
        RUNTIME_DECORATOR_CALLABLE_EXPRESSION_PROP_KEY_PATHS[decoratorName],
        `${nodePath}.x-decorator-props`,
        issues,
      );
      if (decoratorName === 'FormItem') {
        findRuntimeDirectExpressionPaths(
          decoratorProps.rules,
          `${nodePath}.x-decorator-props.rules`,
        ).forEach((expressionPath) => {
          pushRuntimeIssue(
            issues,
            'invalid-runtime-document',
            expressionPath,
            'Runtime v1 表达式只允许无调用、无赋值的数据读取，事件能力必须由受信组件提供',
          );
        });
      }
    }
    const componentProps = auditNode['x-component-props'];
    if (!isObjectRecord(componentProps)) return;
    if (componentName) {
      if (componentName === 'I18nEditor') {
        (['inSchema', 'outSchema'] as const).forEach((propertyName) => {
          if (!Object.prototype.hasOwnProperty.call(componentProps, propertyName)) return;
          const nestedSchema = componentProps[propertyName];
          const nestedSchemaPath = `${nodePath}.x-component-props.${propertyName}`;
          validateRuntimeStaticComponentNames(nestedSchema, issues, nestedSchemaPath);
          // 多语言编辑器的内外层 Schema 会独立交给 Formily 渲染，必须按新的 Schema 根重新审计。
          validateRuntimeComponentSecurity(nestedSchema, issues, nestedSchemaPath);
        });
      }
      if (Object.prototype.hasOwnProperty.call(componentProps, 'component')) {
        const { component } = componentProps;
        if (
          component !== false
          && !(
            typeof component === 'string'
            && (
              isRuntimeSchemaExpression(component)
              || RUNTIME_SAFE_NATIVE_COMPONENT_TAGS.has(component)
            )
          )
        ) {
          pushRuntimeIssue(
            issues,
            'invalid-runtime-document',
            `${nodePath}.x-component-props.component`,
            'Runtime component 只允许 false 或安全的原生布局标签',
          );
        }
      }
      validateRuntimeCallablePropPaths(
        componentProps,
        RUNTIME_COMPONENT_CALLABLE_EXPRESSION_PROP_PATHS[componentName],
        `${nodePath}.x-component-props`,
        issues,
      );
      validateRuntimeCallablePropKeyPaths(
        componentProps,
        RUNTIME_COMPONENT_CALLABLE_EXPRESSION_PROP_KEY_PATHS[componentName],
        `${nodePath}.x-component-props`,
        issues,
      );
      RUNTIME_NAVIGATION_PROP_KEYS[componentName]?.forEach((propertyName) => {
        if (!Object.prototype.hasOwnProperty.call(componentProps, propertyName)) return;
        const navigationValue = componentProps[propertyName];
        const navigationPath = `${nodePath}.x-component-props.${propertyName}`;
        const navigationExpressionPaths = findRuntimeSchemaExpressionPaths(
          navigationValue,
          navigationPath,
        );
        if (navigationExpressionPaths.length > 0) {
          // 导航表达式会在 Formily 求值后携带 Store 数据离开宿主，持久化文档必须只保存静态地址。
          navigationExpressionPaths.forEach((expressionPath) => {
            pushRuntimeIssue(
              issues,
              'invalid-runtime-document',
              expressionPath,
              'Runtime 导航地址不允许使用数据表达式',
            );
          });
          return;
        }
        const navigationIsSafe = propertyName === 'ping'
          ? isSafeRuntimeNavigationPing(navigationValue)
          : isSafeRuntimeNavigationHref(resolveRuntimeNavigationHref(navigationValue));
        if (navigationIsSafe) return;
        pushRuntimeIssue(
          issues,
          'invalid-runtime-document',
          navigationPath,
          'Runtime 导航只允许相对地址或 http、https、mailto、tel 协议',
        );
      });
      RUNTIME_SEARCH_REQUEST_PROP_KEYS[componentName]?.forEach((propertyName) => {
        if (!Object.prototype.hasOwnProperty.call(componentProps, propertyName)) return;
        if (
          componentName === 'SearchSelect'
          && propertyName === 'labelAPI'
          && typeof componentProps[propertyName] === 'boolean'
        ) {
          return;
        }
        validateRuntimeRequestConfig(
          componentProps[propertyName],
          issues,
          `${nodePath}.x-component-props.${propertyName}`,
        );
      });
    }
    if (componentName !== 'Upload') return;
    RUNTIME_UPLOAD_NETWORK_KEYS.forEach((key) => {
      if (!Object.prototype.hasOwnProperty.call(componentProps, key)) return;
      pushRuntimeIssue(
        issues,
        'runtime-unsafe-request-config',
        `${nodePath}.x-component-props.${key}`,
        `Runtime Upload v1 不允许 ${key}，网络上传必须由宿主受信能力接管`,
      );
    });
  });
};
